<template>
  <div class="team_members">
    <div v-if="loading">Loading</div>

    <div v-else>
      <button
        class="button primary"
        v-if="myMembership!.mayAdministrate"
        @click="displaySearch = true"
      >
        {{ $t("teamDetail.addUser.buttonText") }}
      </button>

      <section
        v-if="
          myMembership!.mayAdministrate && invitedUserMemberships.length > 0
        "
      >
        <h4>{{ $t("teamDetail.invitedUsers.title") }}</h4>
        <UserMembershipsList
          :memberships="invitedUserMemberships!"
          :isAdmin="myMembership!.mayAdministrate"
          :showLicenseStatus="!isPitchviewTeam(tenantId)"
          @removeUser="removeUserFromTeam"
        />
      </section>

      <section v-if="activeUserMemberships.length > 0">
        <!-- Active Users -->
        <h4>{{ $t("teamDetail.members.title") }}</h4>
        <UserMembershipsList
          :memberships="activeUserMemberships!"
          :isAdmin="myMembership!.mayAdministrate"
          :showLicenseStatus="!isPitchviewTeam(tenantId)"
          @removeUser="removeUserFromTeam"
          @reloadData="executeQuery"
        />
      </section>

      <template v-if="displaySearch">
        <TenantUserSearch
          :teamId="teamId"
          :tenantId="tenantId"
          @submit="inviteUsers"
          @close="displaySearch = false"
        ></TenantUserSearch>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, ComputedRef } from "vue";
import { TenantUserSearch } from "@/components";
import UserMembershipsList from "./UserMembershipsList.vue";
import { useRoute } from "vue-router";
import {
  useTeamDetailsByIdQuery,
  useRemoveUserFromTeamMutation,
  useSendInvitationsMutation,
  Membership,
} from "@/graphql/operations";

import {
  isPitchviewTeam,
  isVisibleTeamMember,
} from "@/util/team-member-presentation";

const route = useRoute();

const displaySearch = ref<boolean>(false);

const teamId = route.params.id as string;
const {
  data: teamResults,
  fetching: loading,
  executeQuery,
} = useTeamDetailsByIdQuery({
  variables: {
    id: teamId,
  },
  requestPolicy: "cache-and-network",
});

// extract team and memberships
const team = computed(() => teamResults?.value?.team);

const activeUserMemberships: ComputedRef<Membership[]> = computed(() => {
  if (team.value?.activeUserMemberships?.nodes) {
    return (team.value.activeUserMemberships.nodes as Membership[]).filter(
      (membership) =>
        isVisibleTeamMember(team.value?.tenant?.id, membership?.user?.email),
    );
  } else {
    return [];
  }
});

const invitedUserMemberships: ComputedRef<Membership[]> = computed(() => {
  if (team.value?.invitedUserMemberships?.nodes) {
    return (team.value.invitedUserMemberships.nodes as Membership[]).filter(
      (membership) =>
        isVisibleTeamMember(team.value?.tenant?.id, membership?.user?.email),
    );
  } else {
    return [];
  }
});

const myMembership = computed(() => team.value?.myMembership);
const tenantId = computed(() => team.value?.tenant?.id!);

const { executeMutation: sendInvitations } = useSendInvitationsMutation();

function inviteUsers(payload: any) {
  //TODO error handling
  sendInvitations(payload);
  displaySearch.value = false;
}

const { executeMutation: removeUserFromTeam } = useRemoveUserFromTeamMutation();
</script>

<style lang="scss" scoped>
.team_members {
  padding: 1em 0;
}

h4 {
  font-weight: 500;
  margin: 0;
  line-height: 2.8;
}

.button {
  float: right;
  font-size: 0.9em;
}
</style>
